import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import {
  useDeleteStoryDraft,
  useSaveStoryDraft,
  useSubmitStoryReview,
  type StoryReview,
} from "@/lib/api/mentor-story-review";
import { uploadMentorStoryFile } from "@/lib/api/mentor-stories";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import {
  blockedStoryKeys,
  draftEdited,
  emptyDraft,
  filledPhotoCount,
  openNote,
  sectionComplete,
  STORY_COLORS,
  STORY_KEYS,
  STORY_META,
  storyColor,
  storyLocked,
  setStoryPhoto,
  storyReady,
  type StoryDraft,
  type StoryKey,
  type StoryPhoto,
} from "@/lib/mentor-stories/story-content";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { cn } from "@/lib/utils";
import { validateFile } from "@/lib/utils/file-validation";
import { processImage } from "@/lib/utils/image";
import { Button, IconButton } from "@somethings-ui/web";
import {
  ArrowRight,
  Check,
  ChevronDown,
  CircleAlert,
  Loader2,
  Lock,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { StoryPreview } from "./story-preview";
import {
  forgetStoryPhoto,
  releaseStoryPhotos,
  rememberStoryPhoto,
  uploadRenderedSlides,
} from "./story-slides";

const ACCEPTED_IMAGE_TYPES = "image/png,image/jpeg,image/heic,image/heif";

type SectionName = "color" | "caption" | "photos";

interface StoryEditorDialogProps {
  userId: string;
  review: StoryReview | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StoryEditorDialog({
  userId,
  review,
  open,
  onOpenChange,
}: StoryEditorDialogProps) {
  const { toast } = useToast();
  const { track } = useTrackEvent();
  const saveDraft = useSaveStoryDraft();
  const submitReview = useSubmitStoryReview();
  const deleteDraft = useDeleteStoryDraft();
  const [baseline] = useState<StoryDraft>(
    () => review?.draft ?? emptyDraft(),
  );
  const [draft, setDraft] = useState<StoryDraft>(baseline);
  const [storyKey, setStoryKey] = useState<StoryKey>("my_vibe");
  const [section, setSection] = useState<SectionName>("color");
  const [confirmingExit, setConfirmingExit] = useState(false);
  const [uploadingSlots, setUploadingSlots] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [submitting, setSubmitting] = useState(false);
  const uploading = uploadingSlots.size > 0;
  const busy =
    uploading ||
    submitting ||
    saveDraft.isPending ||
    submitReview.isPending ||
    deleteDraft.isPending;

  useEffect(() => releaseStoryPhotos, []);

  const navUnlocked = review?.navUnlocked === true;
  const comments = review?.comments ?? [];
  const blocked = blockedStoryKeys(draft, comments);
  const ready = storyReady(draft) && blocked.length === 0;
  const storyIndex = STORY_KEYS.indexOf(storyKey);
  const showNext = !navUnlocked && storyKey !== "more_about_me";
  const examples = STORY_EXAMPLES[storyKey];
  const nextEnabled = sectionComplete(draft, storyKey);
  const note = openNote(comments, storyKey);

  const close = () => {
    setConfirmingExit(false);
    onOpenChange(false);
  };

  const requestClose = () => {
    if (uploading) return;
    if (draftEdited(draft, baseline)) {
      setConfirmingExit(true);
      return;
    }
    close();
  };

  const persist = async (next: StoryDraft) => {
    const saved = await saveDraft.mutateAsync(next);
    setDraft(saved.draft);
    return saved;
  };

  const handleSave = async () => {
    try {
      await persist(draft);
      track(PostHogEvents.MENTOR_STORY_DRAFT_SAVED, { mentor_id: userId });
      toast({ title: "Story draft saved." });
      close();
    } catch {
      toast({
        title: "Could not save the draft",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    try {
      if (review) await deleteDraft.mutateAsync();
      track(PostHogEvents.MENTOR_STORY_DRAFT_DELETED, { mentor_id: userId });
      toast({ title: "Draft deleted." });
      close();
    } catch {
      toast({
        title: "Could not delete the draft",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async () => {
    if (!ready || submitting) return;
    setSubmitting(true);
    try {
      const slides = await uploadRenderedSlides(draft);
      await persist({ ...draft, slides });
      await submitReview.mutateAsync();
      track(PostHogEvents.MENTOR_STORY_SUBMITTED_FOR_REVIEW, {
        mentor_id: userId,
      });
      close();
    } catch {
      toast({
        title: "Could not submit the story",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const selectStory = (key: StoryKey) => {
    if (storyLocked(draft, key, navUnlocked)) return;
    setStoryKey(key);
    setSection("color");
  };

  const goNext = () => {
    const next = STORY_KEYS[storyIndex + 1];
    if (!next || !nextEnabled) return;
    track(PostHogEvents.MENTOR_STORY_NEXT_STORY_CLICKED, {
      mentor_id: userId,
      story_number: STORY_META[storyKey].number,
    });
    selectStory(next);
  };

  const updateSection = (
    key: StoryKey,
    patch: Partial<StoryDraft[StoryKey]>,
  ) => {
    setDraft((current) => ({
      ...current,
      [key]: { ...current[key], ...patch },
    }));
  };

  const setSlotUploading = (slot: string, uploading: boolean) => {
    setUploadingSlots((current) => {
      const next = new Set(current);
      if (uploading) next.add(slot);
      else next.delete(slot);
      return next;
    });
  };

  const uploadPhoto = async (key: StoryKey, slot: string, file: File) => {
    const { valid, error } = validateFile(file, {
      accept: ACCEPTED_IMAGE_TYPES,
      maxSize: 15 * 1024 * 1024,
    });
    if (!valid) {
      toast({ title: error, variant: "destructive" });
      return;
    }
    setSlotUploading(slot, true);
    try {
      const previous = draft[key].photos.find((photo) => photo.slot === slot);
      const processed = await processImage(file);
      const uploaded = await uploadMentorStoryFile(processed);
      if (previous && previous.key !== uploaded.key) forgetStoryPhoto(previous.key);
      rememberStoryPhoto(uploaded.key, processed);
      const photo: StoryPhoto = { key: uploaded.key, slot, url: uploaded.url };
      setDraft((current) => setStoryPhoto(current, key, photo.slot, photo));
    } catch {
      toast({ title: "Could not upload that photo", variant: "destructive" });
    } finally {
      setSlotUploading(slot, false);
    }
  };

  const removePhoto = (slot: string) => {
    const key = storyKey;
    const existing = draft[key].photos.find((photo) => photo.slot === slot);
    if (existing) forgetStoryPhoto(existing.key);
    setDraft((current) => setStoryPhoto(current, key, slot));
  };

  const meta = STORY_META[storyKey];
  const current = draft[storyKey];

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : requestClose())}>
        <DialogContent
          hideCloseButton
          className="flex h-[min(1040px,calc(100vh-32px))] max-w-[1180px] flex-col gap-xl overflow-hidden rounded-2xl border-0 bg-surfaces-z0 px-2xl pb-0 pt-2xl shadow-high"
        >
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="font-heading text-header-4 text-text-primary">
                {comments.some((comment) => comment.status === "open")
                  ? "Update your stories"
                  : "Create your stories"}
              </DialogTitle>
              <DialogDescription className="mt-xs font-body text-body-1 text-text-secondary">
                Please make sure all 3 stories are complete before submitting.
              </DialogDescription>
            </div>
            <IconButton
              aria-label="Close"
              variant="tertiary"
              size="md"
              disabled={uploading}
              icon={<X className="size-5" />}
              onClick={requestClose}
            />
          </div>

          <div className="flex gap-md">
            {STORY_KEYS.map((key) => {
              const item = STORY_META[key];
              const locked = storyLocked(draft, key, navUnlocked);
              const done = sectionComplete(draft, key) && !blocked.includes(key);
              const flagged = blocked.includes(key);
              const selected = key === storyKey;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={locked}
                  onClick={() => selectStory(key)}
                  className={cn(
                    "relative flex w-44 flex-col items-start rounded-xl px-lg py-md text-left",
                    selected
                      ? "bg-surfaces-z3 ring-2 ring-inset ring-text-primary"
                      : "ring-1 ring-inset ring-surfaces-z5",
                    locked && "opacity-45",
                  )}
                >
                  <span className="font-mono text-body-4 text-text-secondary">
                    STORY {item.index}
                  </span>
                  <span className="mt-xs flex items-center gap-sm font-body text-body-1 font-bold text-text-primary">
                    {item.title}
                    {locked ? <Lock className="size-3.5" aria-hidden /> : null}
                    {done ? (
                      <span className="flex size-5 items-center justify-center rounded-full bg-states-success">
                        <Check className="size-3.5 text-buttonText-primary" aria-label="Complete" />
                      </span>
                    ) : null}
                  </span>
                  {flagged ? (
                    <span
                      className="absolute right-3 top-2.5 size-3 rounded-full bg-[#e5484d] ring-2 ring-white"
                      aria-label="Comment"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="flex min-h-0 flex-1 gap-2xl overflow-hidden">
            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
              {note ? (
                <div className="mb-xl flex items-start gap-md rounded-xl border border-[#ffc9d1] bg-[#ffeaee] px-xl py-lg">
                  <CircleAlert className="size-5 shrink-0 text-text-primary" />
                  <div className="flex flex-col gap-sm">
                    <p className="font-body text-body-1 font-bold text-text-primary">
                      Comment from your lead mentor
                    </p>
                    <p className="font-body text-body-1 leading-6 text-text-secondary">
                      {note.note}
                    </p>
                  </div>
                </div>
              ) : null}
              <h3 className="font-heading text-header-2 text-text-primary">
                {meta.title}
              </h3>
              <p className="mt-sm font-body text-body-1 text-text-secondary">
                {meta.blurb}
              </p>
              <div className="mt-xl border-t border-surfaces-z5">
                <AccordionRow
                  number="1"
                  title="Pick a color for this story"
                  open={section === "color"}
                  done={Boolean(current.color)}
                  summary={
                    storyColor(current.color) ? (
                      <span className="flex items-center gap-sm">
                        <span
                          className="size-3 rounded-full"
                          style={{ background: storyColor(current.color)?.swatch }}
                        />
                        {storyColor(current.color)?.label}
                      </span>
                    ) : null
                  }
                  onToggle={() => setSection("color")}
                >
                  <div className="flex gap-md pl-2xl pt-lg">
                    {STORY_COLORS.map((color) => (
                      <button
                        key={color.id}
                        type="button"
                        aria-label={color.label}
                        className={cn(
                          "size-10 rounded-full ring-1 ring-inset ring-text-primary/15",
                          current.color === color.id && "ring-2 ring-text-primary ring-offset-2",
                        )}
                        style={{ background: color.swatch }}
                        onClick={() => updateSection(storyKey, { color: color.id })}
                      />
                    ))}
                  </div>
                </AccordionRow>
                <AccordionRow
                  number="2"
                  title="Pick your caption"
                  open={section === "caption"}
                  done={Boolean(current.caption?.trim())}
                  summary={current.caption?.trim().toLowerCase()}
                  onToggle={() => setSection("caption")}
                >
                  <div className="mt-lg pl-2xl">
                    <Select
                      value={current.caption || undefined}
                      onValueChange={(caption) => updateSection(storyKey, { caption })}
                    >
                      <SelectTrigger
                        aria-label="Choose a caption"
                        className="h-12 w-full max-w-sm rounded-xl border-surfaces-z5 bg-surfaces-z0 px-md font-body text-body-1"
                      >
                        <SelectValue placeholder="Choose a caption" />
                      </SelectTrigger>
                      <SelectContent className="z-[60] rounded-xl border-surfaces-z5 font-body">
                        {(current.caption &&
                        !meta.captions.some((option) => option === current.caption)
                          ? [current.caption, ...meta.captions]
                          : meta.captions
                        ).map((caption) => (
                          <SelectItem key={caption} value={caption}>
                            {caption}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </AccordionRow>
                <AccordionRow
                  number="3"
                  title="Upload your images"
                  open={section === "photos"}
                  done={
                    filledPhotoCount(current, storyKey) === meta.slots.length
                  }
                  summary={
                    filledPhotoCount(current, storyKey) > 0
                      ? `${filledPhotoCount(current, storyKey)}/${meta.slots.length} photos`
                      : null
                  }
                  onToggle={() => setSection("photos")}
                >
                  <p className="mt-lg pl-2xl font-body text-body-1 text-text-secondary">
                    Each image here matches a frame on your story page.
                  </p>
                  <div className="mt-lg flex flex-wrap gap-md pl-2xl">
                    {meta.slots.map((slot) => {
                      const photo = current.photos.find(
                        (item) => item.slot === slot.slot,
                      );
                      return (
                        <PhotoSlot
                          key={slot.slot}
                          label={slot.label}
                          url={photo?.url}
                          uploading={uploadingSlots.has(slot.slot)}
                          onPick={(file) => void uploadPhoto(storyKey, slot.slot, file)}
                          onRemove={() => removePhoto(slot.slot)}
                        />
                      );
                    })}
                  </div>
                  {examples ? <StoryExamples groups={examples} /> : null}
                </AccordionRow>
              </div>
            </div>
            <div className="hidden min-h-0 shrink-0 overflow-y-auto lg:block">
              <StoryPreview storyKey={storyKey} section={current} />
            </div>
          </div>

          <div className="relative z-10 -mx-2xl flex shrink-0 items-center justify-end gap-lg border-t border-surfaces-z5 bg-surfaces-z0 px-2xl py-lg">
            <Button
              size="lg"
              variant="secondary"
              fullWidth={false}
              disabled={busy}
              loading={saveDraft.isPending && !submitting}
              onPress={() => void handleSave()}
            >
              Save draft
            </Button>
            {showNext ? (
              <Button
                size="lg"
                fullWidth={false}
                disabled={!nextEnabled || busy}
                trailingIcon={<ArrowRight className="size-6" />}
                onPress={goNext}
              >
                Next story
              </Button>
            ) : (
              <Button
                size="lg"
                fullWidth={false}
                disabled={!ready || busy}
                loading={submitting}
                onPress={() => void handleSubmit()}
              >
                Submit for review
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmingExit} onOpenChange={setConfirmingExit}>
        <DialogContent className="max-w-[440px] rounded-2xl">
          <DialogTitle className="font-heading text-header-4">
            Before you go
          </DialogTitle>
          <DialogDescription className="font-body text-body-2 text-text-secondary">
            Save this draft, or delete it and start over.
          </DialogDescription>
          <div className="flex justify-end gap-md">
            <Button
              size="sm"
              variant="destructive"
              fullWidth={false}
              disabled={busy}
              loading={deleteDraft.isPending}
              onPress={() => void handleDelete()}
            >
              Delete draft
            </Button>
            <Button
              size="sm"
              fullWidth={false}
              disabled={busy}
              loading={saveDraft.isPending && !submitting}
              onPress={() => void handleSave()}
            >
              Save draft
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface ExampleGroup {
  images: readonly string[];
  label: string;
  tall: boolean;
}

const STORY_EXAMPLES: Partial<Record<StoryKey, readonly ExampleGroup[]>> = {
  my_faves: [
    { images: ["snack-1.jpg", "snack-2.jpg", "snack-3.jpg"], label: "Snacks", tall: false },
    { images: ["drink-1.jpg", "drink-2.jpg", "drink-3.jpg"], label: "Drinks", tall: false },
    { images: ["album-1.jpg", "album-2.jpg", "album-3.jpg"], label: "Album", tall: false },
    { images: ["movie-1.jpg", "movie-2.jpg", "movie-3.jpg"], label: "Movie / TV", tall: true },
  ],
  my_vibe: [
    { images: ["vacation-1.jpg", "vacation-2.jpg", "vacation-3.jpg"], label: "Dream vacation", tall: false },
    { images: ["meme-1.png", "meme-2.jpg", "meme-3.jpg"], label: "Meme", tall: false },
    { images: ["character-1.jpg", "character-2.jpg", "character-3.jpg"], label: "Movie character", tall: false },
  ],
};

function StoryExamples({ groups }: { groups: readonly ExampleGroup[] }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="mt-lg flex flex-col gap-md pl-2xl">
      <button
        type="button"
        className="flex items-center gap-sm"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="font-body text-body-1 font-bold text-text-primary">See example images</span>
        <ChevronDown className={cn("size-5", open && "rotate-180")} />
      </button>
      {open ? (
        <div className="flex flex-col gap-lg">
          {groups.map((group) => (
            <div key={group.label} className="flex flex-col gap-sm">
              <p className="font-body text-body-2 text-text-secondary">{group.label}</p>
              <div className="flex gap-sm">
                {group.images.map((image) => (
                  <div
                    key={image}
                    className={cn(
                      "overflow-hidden rounded-md border border-surfaces-z5 bg-surfaces-z3",
                      group.tall ? "h-[142px] w-24" : "size-24",
                    )}
                  >
                    <img
                      src={`/mentor-stories/examples/${image}`}
                      alt=""
                      className="size-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function AccordionRow({
  number,
  title,
  open,
  done,
  summary,
  onToggle,
  children,
}: {
  number: string;
  title: string;
  open: boolean;
  done: boolean;
  summary?: ReactNode;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className="border-t border-surfaces-z5 py-xl first:border-t-0">
      <button
        type="button"
        className="flex w-full items-center gap-md text-left"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span
          className={cn(
            "flex size-7 items-center justify-center rounded-full font-mono text-body-2 text-buttonText-primary",
            done ? "bg-states-success" : "bg-text-primary",
          )}
        >
          {done ? <Check className="size-4" /> : number}
        </span>
        <span
          className={cn(
            "font-heading text-header-3",
            open ? "text-text-primary" : "text-text-secondary",
          )}
        >
          {title}
        </span>
        <span className="ml-auto flex items-center gap-md">
          {!open && summary ? (
            <span className="rounded-md bg-surfaces-z3 px-md py-xs font-body text-body-1 text-text-primary">
              {summary}
            </span>
          ) : null}
          <ChevronDown className={cn("size-5 text-text-primary", open && "rotate-180")} />
        </span>
      </button>
      {open ? children : null}
    </div>
  );
}

function PhotoSlot({
  label,
  url,
  uploading,
  onPick,
  onRemove,
}: {
  label: string;
  url?: string;
  uploading: boolean;
  onPick: (file: File) => void;
  onRemove: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const signed = useSignedMedia(url);

  return (
    <div className="relative w-[125px] overflow-hidden rounded-xl border border-surfaces-z5 bg-surfaces-z0">
      <button
        type="button"
        className="flex h-[108px] w-full items-center justify-center overflow-hidden bg-surfaces-z3"
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? (
          <Loader2 className="size-5 animate-spin" />
        ) : signed.url ? (
          <img src={signed.url} alt="" className="size-full object-cover" />
        ) : (
          <span className="flex size-8 items-center justify-center rounded-full border border-text-primary">
            <Plus className="size-4" />
          </span>
        )}
      </button>
      {url ? (
        <IconButton
          aria-label={`Remove ${label}`}
          variant="secondary"
          size="sm"
          className="absolute right-2 top-2"
          icon={<Trash2 className="size-3.5" />}
          onClick={onRemove}
        />
      ) : null}
      <p className="px-md pb-md pt-md text-center font-body text-body-2 font-bold text-text-primary">
        {label}
      </p>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onPick(file);
          event.target.value = "";
        }}
      />
    </div>
  );
}
